import { useEffect, useRef, useState } from "react"
import { Link, useLocation, useNavigate, useOutletContext, useParams } from "react-router-dom"
import ChargerType from "../../types/ChargerType"
import Comm from "../../Comm"
import styled from "styled-components"
import { BREAKPOINTS, TAP_MIN } from "../../GlobalTheme"
import useScrollSelectedIntoView from "../../utils/useScrollSelectedIntoView"
import { useTranslation } from "react-i18next"
import GeneralInformation from "./GeneralInformation"
import Charges from "./Charges"
import Statistics from "./Statistics"
import UsersRfid from "./UsersRfid"
import Logs from "./Logs"
import Settings from "./Settings"
import PushNotifications from "./PushNotifications"
import ExtLogUpload from "./ExtLogUpload"
import Icon from "../../Icon"
import { child, get, getDatabase, ref } from "firebase/database"
import fbConf from "../../FirebaseConf"
import { getAuth } from "firebase/auth"
import Select from "../../components/Select"
import React from "react"
import UpgradeHistory from "./UpgradeHistory"
import Update from "./Update"
import { setPageTitle, chargerTitleName } from "../../utils/pageTitle"

interface MenuElementType {
  id: string,
  isOfflineSafe: boolean,
  adminOnly?: boolean
}

const MenuContainer = styled.div`
  position: absolute;
  top: -1.5vh;
  display: flex;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    /* Sticks to the top of the page scroller. Scrolled normally the strip slid up under the fixed
       header and, part-way, showed nothing but the active tab's underline floating in an empty
       band. Keeping it pinned also keeps the tabs reachable while reading down the page. */
    position: sticky;
    top: 0px;
    z-index: 2;
    background-color: ${props => props.theme.colors.background};
    align-items: center;
    max-width: 100%;
    overflow-x: auto;
    overscroll-behavior-x: contain;
    padding-bottom: 6px;
  }
`

const MenuElement = styled.p<{ $selected: boolean, $isDisabled: boolean }>`
  font-weight: 600;
  font-size: 12px;
  color: ${props => props.$selected ? props.theme.colors.gray_900 : props.$isDisabled ? props.theme.colors.gray_400 : props.theme.colors.gray_700};
  margin: 0px;
  margin-left: 10px;
  margin-right: 10px;
  padding-bottom: 2px;
  border-bottom: ${props => props.$selected ? `2px solid ${props.theme.colors.primary}` : '0px solid black'};
  cursor: ${props => props.$isDisabled ? 'default' : 'pointer'};

  @media (max-width: ${BREAKPOINTS.shell}px) {
    white-space: nowrap;
    min-height: ${TAP_MIN}px;
    display: flex;
    align-items: center;
  }
`

const ChargerSelectorContainer = styled.div`
  position: absolute;
  top: -7vh;
  left: 1vw;
  width: max(27vw, 345.6px);
  max-height: 95dvh;
  overflow-y: scroll;
  display: flex;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    position: static;
    top: auto;
    left: auto;
    width: 100%;
    max-height: none;
    overflow-y: visible;
    align-items: center;
    margin-bottom: 12px;
  }
`

const ChargerIdLabelForAdmins = styled.p`
  font-weight: 600;
  font-size: 12px;
  color: ${props => props.theme.colors.gray_900};
  margin-left: 12px;
`

export default function Charger() {
  const { t, i18n } = useTranslation()
  const { chargerId, subpath } = useParams()
  const { isAdmin } = useOutletContext<{ isAdmin: boolean }>()
  const navigate = useNavigate()
  const location = useLocation()

  const menuElements: MenuElementType[] = [
    {
      id: 'generalInformation',
      isOfflineSafe: true
    }, {
      id: 'charges',
      isOfflineSafe: true
    }, {
      id: 'statistics',
      isOfflineSafe: true,
      adminOnly: true
    }, {
      id: 'usersRfid',
      isOfflineSafe: false
    }, {
      id: 'logs',
      isOfflineSafe: true,
      adminOnly: true,
    }, {
      id: 'settings',
      isOfflineSafe: false
    }, {
      id: 'pushNotifications',
      isOfflineSafe: false
    }, {
      id: 'extLogUpload',
      isOfflineSafe: false,
      adminOnly: true
    }, {
      id: 'update',
      isOfflineSafe: true,
      adminOnly: true
    }, {
      id: 'upgradeHistory',
      isOfflineSafe: true,
      adminOnly: true
    }
  ]

  const [charger, setCharger] = useState<ChargerType | null>()
  const [page, setPage] = useState<string>(menuElements[0].id)
  const [chargers, setChargers] = useState<{ id: string, name: string }[]>()

  async function fetchCharger() {
    const response = await Comm('/api-charger/charger', {
      headers: { "Charger_id": chargerId, "Content-Type": "application/json" }
    })
    const result = await response.json()

    // Get the firebase name of the charger if the user is not admin
    if (!isAdmin) {
      try {
        const firebaseChargerNameResponse = await get(child(ref(getDatabase(fbConf)), `voltie/chargerusers/${getAuth(fbConf).currentUser?.uid}/chargers/${result.chargerId}/name`))

        //const firebaseChargerNameResponse = await get(child(ref(getDatabase(fbConf)), `voltie/chargerusers/${result.userId}/chargers/${result.chargerId}/name`))
        result.localName = await firebaseChargerNameResponse.val() || result.localName
      } catch (e) { console.log(e) }
    }

    // Ping charger to determine if actually online
    const isOnline = await Comm('/api-websocket/mobile', {
      method: "POST",
      headers: { "Charger_id": chargerId, "Content-Type": "application/json" },
      body: JSON.stringify({ "msg_type": 11 })
    })
    if (isOnline.status == 200) {
      result.online = true
    } else {
      result.online = false
    }
    setCharger(result)
  }
  useEffect(() => {
    fetchCharger()
    if (!isAdmin) {
      fetchChargersForChargerSelector()
    }
  }, [])

  useEffect(() => {
    if (subpath) {

      // If the entered subpath is not in the menu, redirect to 404
      if (!menuElements.find(element => element.id === subpath)) {
        navigate('/404')
      } else {
        setPage(subpath)
      }

    } else {
      setPage('generalInformation')
    }
  }, [location])

  // Fetch all of the users chargers to show in the chargers dropdown selector, only for users
  async function fetchChargersForChargerSelector() {

    // Fetch chargers
    let chargers = []
    const chargersResponse = await Comm('/api-charger/user/chargers', {})
    if (chargersResponse.status != 200) { return }
    const chargersResult = await chargersResponse.json()

    if (chargersResult.length > 0) {
      // Get users chargers from firebase to get the firebase charger name
      const firebaseChargerNamesResponse = await get(child(ref(getDatabase(fbConf)), `voltie/chargerusers/${chargersResult[0].userId}/chargers`))
      const firebaseChargerNames = await firebaseChargerNamesResponse.val()
      for (const charger of chargersResult) {
        charger.localName = firebaseChargerNames[charger.chargerId]?.name || charger.localName
      }
    }
    chargers = chargersResult


    const result: { id: string, name: string }[] = []
    for (const charger of chargers) {
      result.push({ id: charger.chargerId, name: charger.localName })
    }
    setChargers(result.sort((a, b) => a.name.localeCompare(b.name)))
  }

  useEffect(() => {
    console.log(chargerId)
    if (charger && chargerId != charger.chargerId) {
      //window.location.reload()
      setCharger(null)
      fetchCharger()
    }
  }, [chargerId])

  // Reflect the charger name and current sub-page in the tab title, e.g. "Tomi's charger - Settings" (VLT-1973)
  useEffect(() => {
    if (charger) setPageTitle(`${chargerTitleName(charger.localName, charger.chargerId)} - ${t(`charger_${page}`)}`)
  }, [charger, page, i18n.language])


  const menuContainerRef = useRef<HTMLDivElement>(null)
  const selectedTabRef = useRef<HTMLTableCellElement | null>(null)
  useScrollSelectedIntoView(menuContainerRef, selectedTabRef, page)

  return (
    <React.Fragment key={charger?.chargerId}>
      <MenuContainer ref={menuContainerRef}>
        <Icon path="general.backArrow" width="16px" height="16px" margintop="3px" onClick={() => navigate('/chargers')} />
        <table>
          <tbody>
            <tr>
              {menuElements.map(menuElement => (menuElement.adminOnly ? isAdmin : true) && (
                <td key={menuElement.id} ref={el => { if (page == menuElement.id) selectedTabRef.current = el }}>
                  {(!(charger ? (!charger.online && !menuElement.isOfflineSafe) : true) || isAdmin) ? (
                    <Link to={`/chargers/${chargerId}${menuElement.id == 'generalInformation' ? '' : `/${menuElement.id}`}`} style={{ textDecoration: 'none' }}>
                      <MenuElement $isDisabled={charger ? (!charger.online && !menuElement.isOfflineSafe) : true} $selected={page == menuElement.id}>{t(`charger_${menuElement.id}`)}</MenuElement>
                    </Link>
                  ) : (
                    <MenuElement $isDisabled={true} $selected={page == menuElement.id}>{t(`charger_${menuElement.id}`)}</MenuElement>
                  )}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </MenuContainer>
      {!isAdmin && charger && chargers && <>
        <ChargerSelectorContainer>
          <Icon path="general.backArrow" width="18px" height="18px" margintop="4px" marginright="6px" color="gray_600" onClick={() => { try { navigate(`/chargers/${chargers[chargers.findIndex(charger => charger.id == chargerId) - 1].id}${subpath ? `/${subpath}` : ''}`) } catch (err) {}}} />
          <Select width={"min(max(25vw, 320px), calc(100vw - 120px))"} options={[`${charger.chargerId} - ${charger.localName}`, ...chargers.map(charger => `${charger.id} - ${charger.name}`)]} handleSelect={(value) => { navigate(`/chargers/${value.split(' - ')[0]}${subpath ? `/${subpath}` : ''}`) }} />
          <Icon path="general.forwardArrow" width="18px" height="18px" margintop="4px" marginleft="32px" color="gray_600" onClick={() => { try { navigate(`/chargers/${chargers[chargers.findIndex(charger => charger.id == chargerId) + 1].id}${subpath ? `/${subpath}` : ''}`) } catch (err) {}}} />
        </ChargerSelectorContainer>
      </>}
      {isAdmin && charger && <>
        <ChargerSelectorContainer>
          <ChargerIdLabelForAdmins>{`${charger.chargerId} - ${charger.localName}`}</ChargerIdLabelForAdmins>
        </ChargerSelectorContainer>
      </>}
      {charger && (
        <>
          {page == 'generalInformation' && <GeneralInformation charger={charger} isAdmin={isAdmin} triggerRefreshChargerValues={fetchCharger} />}
          {page == 'charges' && <Charges charger={charger} isAdmin={isAdmin} />}
          {page == 'statistics' && <Statistics />}
          {page == 'usersRfid' && <UsersRfid charger={charger} isAdmin={isAdmin} />}
          {page == 'logs' && <Logs />}
          {page == 'settings' && <Settings charger={charger} isAdmin={isAdmin} />}
          {page == 'pushNotifications' && <PushNotifications charger={charger} />}
          {page == 'extLogUpload' && <ExtLogUpload charger={charger} />}
          {page == 'update' && <Update charger={charger} />}
          {page == 'upgradeHistory' && <UpgradeHistory charger={charger} />}
        </>
      )}
    </React.Fragment>
  )
}